// Copyright (c) Ping. All rights reserved.
//
// Shared plain-text body parser for chat bubbles + compact rows. Decodes
// the envelope (so raw `{kind,payload}` JSON never leaks) and splits the
// text into `text` / `mention` parts so both renderers highlight + make
// @mentions tappable.
//
// Detection: EVERY `@handle` token is treated as a mention (highlighted +
// clickable), regardless of whether it matched the message's `mentions`
// array — the array only fails to populate on some receive paths, and a
// mention should always be visible/clickable. The resolved account id is
// used when the handle matches a `mentions` entry; otherwise the bare
// handle is passed so the host can still resolve the user.

import { decodeBubbleBody } from './decodeBubbleBody';

export type MessagePart =
  | { readonly type: 'text'; readonly text: string }
  | { readonly type: 'mention'; readonly text: string; readonly userId: string }
  // A URL — rendered as a tappable, accent-coloured link. `text` is the
  // display label (cleaned, no scheme); `url` is the full target. Covers
  // both bare `https://…` URLs and markdown `[label](url)` syntax so a
  // pasted/auto-wrapped link never renders as raw `[x](x)`.
  | { readonly type: 'link'; readonly text: string; readonly url: string };

export interface EmbeddedLinkPreview {
  readonly url: string;
  readonly title: string;
  readonly description?: string | undefined;
  readonly imageUri?: string | undefined;
}

// Pulls a `payload.link_preview` block out of a `com.ping.text` envelope
// body. The sender embeds the scraped og: metadata here so the text +
// preview render as ONE message (bubble + compact). Returns undefined for
// a plain-string body or any envelope without a preview.
export function extractEmbeddedLinkPreview(body: string): EmbeddedLinkPreview | undefined {
  if (body.length === 0 || body.charAt(0) !== '{') return undefined;
  try {
    const parsed = JSON.parse(body) as {
      payload?: {
        link_preview?: {
          url?: string;
          title?: string;
          desc?: string;
          image_url?: string;
        };
      };
    };
    const lp = parsed.payload?.link_preview;
    if (lp === undefined || typeof lp.url !== 'string' || lp.url.length === 0) {
      return undefined;
    }
    return {
      url: lp.url,
      title: lp.title !== undefined && lp.title.length > 0 ? lp.title : lp.url,
      ...(lp.desc !== undefined && lp.desc.length > 0 ? { description: lp.desc } : {}),
      ...(lp.image_url !== undefined && lp.image_url.length > 0
        ? { imageUri: lp.image_url }
        : {}),
    };
  } catch {
    return undefined;
  }
}

// Single pass over the body matching, in order of appearance:
//   1. markdown link  `[label](https://…)`  → groups 1(all) 2(label) 3(url)
//   2. bare URL        `https://…`           → group 4
//   3. @mention        `@handle`             → group 5
// Stops the bare-URL match at whitespace + bracket/paren/quote punctuation
// so a markdown link's `](` glue is never swallowed into the URL.
const TOKEN_RE =
  /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))|(https?:\/\/[^\s<>"'`)\]]+)|(@\w[\w.-]*)/g;

/** Human-friendly link label: drop the scheme + a trailing slash. */
function cleanLinkLabel(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

export function parseMessageBody(
  rawBody: string,
  mentions: readonly string[] = [],
): MessagePart[] {
  const body = decodeBubbleBody(rawBody);
  const parts: MessagePart[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;
  while ((match = TOKEN_RE.exec(body)) !== null) {
    if (lastIndex < match.index) {
      parts.push({ type: 'text', text: body.slice(lastIndex, match.index) });
    }
    if (match[1] !== undefined) {
      // Markdown link `[label](url)`. Show the label unless it's itself a
      // URL (or equals the target) — then show the cleaned URL.
      const label = match[2] ?? '';
      const url = match[3] ?? '';
      const display =
        label.length > 0 && !/^https?:\/\//i.test(label) ? label : cleanLinkLabel(url);
      parts.push({ type: 'link', text: display, url });
    } else if (match[4] !== undefined) {
      // Bare URL.
      const url = match[4];
      parts.push({ type: 'link', text: cleanLinkLabel(url), url });
    } else if (match[5] !== undefined) {
      // @mention. Prefer the resolved account id from the mentions array;
      // fall back to the bare handle so it stays highlighted + clickable.
      const handle = match[5].slice(1);
      const userId = mentions.find((m) => m === handle || m.endsWith(handle)) ?? handle;
      parts.push({ type: 'mention', text: match[5], userId });
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < body.length) {
    parts.push({ type: 'text', text: body.slice(lastIndex) });
  }
  if (parts.length === 0) parts.push({ type: 'text', text: body });
  return parts;
}
